import React from 'react'
import Box from '@mui/material/Box'
import { styled } from '@mui/material/styles'
import colors from '@/assets/themes/palettes/colors'
import { BirthdayIcon, EmtTheme, EmtTypography, GenderIcon, SizeIcon, EmtIcon } from 'emt-components-v2'
import { useTranslation } from 'next-i18next'
import { DateTime } from 'luxon'
import { useEMTState } from '@/store'
import { AnimalProfile } from '@/dataV3/Animal/AnimalProfile'

type Props = React.PropsWithChildren<{
  profile: Pick<AnimalProfile, 'sizeSlug' | 'birthDate' | 'gender' | 'animalOptions'>
  color?: 'primary' | 'purple'
}>

type EmtThemeType = { theme: EmtTheme }

const StyledDogProperties = styled(Box)<EmtThemeType>(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '25px',
  '& .info-box': {
    flex: 1,
    border: `1px solid ${theme.palette.greyGradient[100]}`,
    borderRadius: '16px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    rowGap: '10px',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    [theme.breakpoints.down('md')]: {
      border: 0,
      boxShadow: '0px 12px 16px rgba(0, 0, 0, 0.08), 0px 4px 56px rgba(0, 0, 0, 0.08)',
    },
  },
  [theme.breakpoints.down('md')]: {
    gap: '20px',
  },
})) as typeof Box

const currentDate = DateTime.now()

export const DogProperties: React.FC<Props> = ({
  profile: { sizeSlug, birthDate, gender, animalOptions },
  color = 'primary',
}) => {
  const { t } = useTranslation('profile')
  const { t: tc } = useTranslation('common')
  const { dogSizes } = useEMTState().configuration

  const dogSize = sizeSlug && dogSizes.length ? dogSizes.find((ds) => ds.id === sizeSlug)?.name : undefined

  const dogAge = DateTime.fromISO(currentDate.toISO())
    .diff(DateTime.fromISO(birthDate as string), ['years', 'months', 'weeks', 'days'])
    .toObject()

  return (
    <StyledDogProperties>
      <Box className="info-box" sx={{ backgroundColor: '#fff', minWidth: '120px' }}>
        <GenderIcon color={colors[`${color}`].main} />
        <Box>
          <EmtTypography variant="body2" color={`${color}.main`} fontWeight={700}>
            {t('dogProperties.gender')}
          </EmtTypography>
          <EmtTypography variant="body2" color="greyGradient.450" fontWeight={400}>
            {t(`dogProperties.${gender}`)}
          </EmtTypography>
        </Box>
      </Box>
      <Box className="info-box" sx={{ backgroundColor: '#fff', minWidth: '120px' }}>
        <BirthdayIcon color={colors[`${color}`].main} />
        <Box>
          <EmtTypography variant="body2" color={`${color}.main`} fontWeight={700}>
            {t('dogProperties.age')}
          </EmtTypography>
          <EmtTypography variant="body2" color="greyGradient.450" fontWeight={400}>
            {dogAge.years
              ? t('dogProperties.year', { count: Math.round(dogAge.years) })
              : dogAge.months
              ? t('dogProperties.month', { count: Math.round(dogAge.months) })
              : dogAge.weeks
              ? t('dogProperties.week', { count: Math.round(dogAge.weeks) })
              : t('dogProperties.week', { count: Math.round(dogAge.days as number) })}
          </EmtTypography>
        </Box>
      </Box>
      <Box className="info-box" sx={{ backgroundColor: '#fff', minWidth: '120px' }}>
        <SizeIcon color={colors[`${color}`].main} />
        <Box>
          <EmtTypography variant="body2" color={`${color}.main`} fontWeight={700}>
            {t('dogProperties.size')}
          </EmtTypography>
          <EmtTypography variant="body2" color="greyGradient.450" fontWeight={400}>
            {dogSize ? dogSize : t('dogProperties.undefined')}
          </EmtTypography>
        </Box>
      </Box>
      <Box className="info-box" sx={{ backgroundColor: '#fff', minWidth: '120px' }}>
        <EmtIcon
          color={colors[`${color}`].main}
          className="emt-denied"
          sx={{ color: colors[`${color}`].main, fontSize: '20px' }}
        />
        <Box>
          <EmtTypography variant="body2" color={`${color}.main`} fontWeight={700}>
            {t('dogProperties.sterilized')}
          </EmtTypography>
          <EmtTypography variant="body2" color="greyGradient.450" fontWeight={400}>
            {animalOptions?.sterilized ? tc('yes') : tc('no')}
          </EmtTypography>
        </Box>
      </Box>
    </StyledDogProperties>
  )
}

export const EditDogProperties: React.FC = ({}) => {
  return <Box>prout</Box>
}
